import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import s from '../styles/AutoloadSettingsModal.module.css';

const ALL_HOURS = Array.from({ length: 24 }, (_, index) => index);
const WEEKDAYS = [
  { value: 0, label: 'Пн' },
  { value: 1, label: 'Вт' },
  { value: 2, label: 'Ср' },
  { value: 3, label: 'Чт' },
  { value: 4, label: 'Пт' },
  { value: 5, label: 'Сб' },
  { value: 6, label: 'Вс' },
];

const normalizeNumberList = (values, allowedValues) => {
  const allowedSet = new Set(allowedValues);
  const normalized = Array.isArray(values)
    ? values
        .map((value) => Number(value))
        .filter((value) => Number.isInteger(value) && allowedSet.has(value))
    : [];

  return [...new Set(normalized)].sort((left, right) => left - right);
};

const createDefaultScheduleItem = () => ({
  rate: 50000,
  weekdays: WEEKDAYS.map((day) => day.value),
  time_slots: [...ALL_HOURS],
});

const createDefaultForm = (project) => ({
  autoload_enabled: true,
  feed_name: '',
  feed_url: project?.xml_url || '',
  report_email: '',
  schedule: [createDefaultScheduleItem()],
});

const normalizeSchedule = (schedule) => {
  if (!Array.isArray(schedule) || !schedule.length) {
    return [createDefaultScheduleItem()];
  }

  const firstItem = schedule[0] || {};

  return [{
    rate: firstItem?.rate ?? createDefaultScheduleItem().rate,
    weekdays: normalizeNumberList(firstItem?.weekdays, WEEKDAYS.map((day) => day.value)),
    time_slots: normalizeNumberList(firstItem?.time_slots, ALL_HOURS),
  }];
};

const getSingleScheduleItem = (schedule) => {
  if (!Array.isArray(schedule) || !schedule.length) {
    return createDefaultScheduleItem();
  }

  const [item] = normalizeSchedule(schedule);
  return item || createDefaultScheduleItem();
};

const buildSingleSchedule = (schedule) => {
  const item = getSingleScheduleItem(schedule);
  return [{
    rate: item?.rate,
    weekdays: normalizeNumberList(item?.weekdays, WEEKDAYS.map((day) => day.value)),
    time_slots: normalizeNumberList(item?.time_slots, ALL_HOURS),
  }];
};

const buildFormFromProfile = (project, profile) => {
  const defaultForm = createDefaultForm(project);
  const firstFeed = Array.isArray(profile?.feeds_data) ? profile.feeds_data[0] || {} : {};

  return {
    autoload_enabled: Boolean(profile?.autoload_enabled),
    feed_name: firstFeed.feed_name || defaultForm.feed_name,
    feed_url: firstFeed.feed_url || defaultForm.feed_url,
    report_email: profile?.report_email || '',
    schedule: buildSingleSchedule(profile?.schedule),
  };
};

const hasProfileData = (profile) => {
  const firstFeed = Array.isArray(profile?.feeds_data) ? profile.feeds_data[0] || {} : {};
  const hasFeedData = Boolean((firstFeed.feed_url || '').trim() || (firstFeed.feed_name || '').trim());
  const hasReportEmail = Boolean((profile?.report_email || '').trim());
  const hasSchedule = Array.isArray(profile?.schedule) && profile.schedule.length > 0;

  return hasFeedData || hasReportEmail || hasSchedule;
};

const toggleValue = (collection, value) => {
  return collection.includes(value)
    ? collection.filter((item) => item !== value)
    : [...collection, value].sort((left, right) => left - right);
};

const AutoloadSettingsModal = ({ project, onClose }) => {
  const defaultForm = useMemo(() => createDefaultForm(project), [project]);

  const [formData, setFormData] = useState(defaultForm);
  const [profileMode, setProfileMode] = useState('ready');
  const [profileLoadedOk, setProfileLoadedOk] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showScheduleEditor, setShowScheduleEditor] = useState(false);

  const [info, setInfo] = useState('');
  const [success, setSuccess] = useState('');
  const [submitError, setSubmitError] = useState('');

  const loadProfile = useCallback(
    async ({ refresh = false } = {}) => {
      if (refresh) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }

      setInfo('');
      setSuccess('');
      setSubmitError('');
      setShowScheduleEditor(false);

      if (!refresh) {
        setFormData(defaultForm);
      }

      try {
        const response = await fetch('/api/projects/autoload/profile', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ project_id: project.id }),
        });

        const result = await response.json().catch(() => ({}));

        if (!response.ok || !result.ok) {
          setProfileMode('error');
          setProfileLoadedOk(false);
          setInfo(result.error || 'Профиль автозагрузки не удалось загрузить.');
          return;
        }

        const profile = result.profile || {};
        const profileHasData = hasProfileData(profile);

        setFormData(buildFormFromProfile(project, profile));
        setProfileMode(profileHasData ? 'ready' : 'empty');
        if (!profileHasData) {
          setInfo('Профиль автозагрузки не создан. Заполните обязательные поля и сохраните настройки.');
        }
        setProfileLoadedOk(true);
      } catch (_error) {
        setProfileMode('error');
        setProfileLoadedOk(false);
        setInfo('Не удалось получить текущие настройки Avito.');
      } finally {
        if (refresh) {
          setIsRefreshing(false);
        } else {
          setIsLoading(false);
        }
      }
    },
    [defaultForm, project]
  );

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.dispatchEvent(new CustomEvent('autoload-modal-visibility', { detail: true }));

    return () => {
      document.body.style.overflow = oldOverflow;
      window.dispatchEvent(new CustomEvent('autoload-modal-visibility', { detail: false }));
    };
  }, []);

  const updateScheduleItem = (index, patch) => {
    setFormData((prev) => ({
      ...prev,
      schedule: prev.schedule.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              ...patch,
            }
          : item
      ),
    }));
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleRateChange = (index, value) => {
    updateScheduleItem(index, { rate: value });
  };

  const handleToggleHour = (index, hour) => {
    updateScheduleItem(index, {
      time_slots: toggleValue(formData.schedule[index].time_slots, hour),
    });
  };

  const handleToggleWeekday = (index, day) => {
    updateScheduleItem(index, {
      weekdays: toggleValue(formData.schedule[index].weekdays, day),
    });
  };

  const handleScheduleBulk = (index, field, values) => {
    updateScheduleItem(index, { [field]: values });
  };

  const validateForm = () => {
    if (!formData.report_email.trim()) {
      return 'Укажите email для отчетов.';
    }

    if (!formData.feed_url.trim()) {
      return 'Укажите ссылку на фид.';
    }

    const invalidRange = formData.schedule.find(
      (item) => !item.weekdays.length || !item.time_slots.length
    );
    if (invalidRange) {
      return 'В настройках расписания нужен хотя бы один день и один час.';
    }

    return '';
  };

  const handleSubmit = async () => {
    setSubmitError('');
    setInfo('');
    setSuccess('');

    const validationError = validateForm();
    if (validationError) {
      setSubmitError(validationError);
      return;
    }

    setIsSaving(true);

    const payload = {
      agreement: true,
      autoload_enabled: formData.autoload_enabled,
      feeds_data: [
        {
          feed_name: (formData.feed_name || '').trim(),
          feed_url: formData.feed_url.trim(),
        },
      ],
      report_email: formData.report_email.trim(),
      schedule: buildSingleSchedule(formData.schedule).map((item) => ({
        rate: Number(item.rate),
        weekdays: item.weekdays,
        time_slots: item.time_slots,
      })),
    };

    try {
      const response = await fetch('/api/projects/autoload/profile/update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          project_id: project.id,
          profile: payload,
        }),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.ok) {
        throw new Error(result.error || `Ошибка ${response.status}`);
      }

      setSuccess('Настройки автозагрузки сохранены.');
      setProfileMode('ready');
    } catch (error) {
      setSubmitError(error.message || 'Не удалось сохранить настройки автозагрузки.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className={s.overlay}
      onClick={(event) => {
        event.stopPropagation();
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <div
        className={s.modal}
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button type="button" onClick={onClose} className={s.closeButton}>
          <FontAwesomeIcon icon={faTimesCircle} />
        </button>

        <div className={s.header}>Настройка автозагрузки</div>

        <div className={s.body}>
          {info ? <div className={s.infoBox}>{info}</div> : null}
          {success ? <div className={s.successBox}>{success}</div> : null}

          {isLoading ? (
            <div className={s.loader}>Загрузка профиля автозагрузки...</div>
          ) : (
            <>
              <section className={s.block}>
                <div className={s.blockHead}>
                  <strong>Данные автозагрузки</strong>
                  <button
                    type="button"
                    className={`${s.btn} ${s.primary}`}
                    onClick={() => loadProfile({ refresh: true })}
                    disabled={isRefreshing || isSaving}
                  >
                    {isRefreshing ? 'Обновление...' : 'Обновить'}
                  </button>
                </div>

                {profileMode === 'error' ? (
                  <div className={s.infoLine}>Профиль недоступен. Проверьте токен и нажмите Обновить.</div>
                ) : (
                  <>
                    {profileMode === 'empty' ? (
                      <div className={s.row}>
                        <span className={`${s.chipStatus} ${s.red}`}>Профиль не создан</span>
                        <span className={s.meta}>Данных автозагрузки пока нет.</span>
                      </div>
                    ) : null}

                    <div className={s.row}>
                      <div className={s.statusWrap}>
                        <input
                          type="checkbox"
                          id={`autoload_status_${project.id}`}
                          name="autoload_enabled"
                          checked={formData.autoload_enabled}
                          onChange={handleChange}
                        />
                        <label htmlFor={`autoload_status_${project.id}`}>Статус</label>
                      </div>
                      <span className={`${s.chipStatus} ${formData.autoload_enabled ? s.green : s.red}`}>
                        {formData.autoload_enabled ? 'Активна' : 'Выключена'}
                      </span>
                    </div>

                    <div className={s.rowCompact}>
                      <span className={s.meta}>Ссылка на фид:</span>
                    </div>
                    <div className={s.row}>
                      <input
                        className={s.input}
                        name="feed_url"
                        type="url"
                        value={formData.feed_url}
                        onChange={handleChange}
                        placeholder="https://.../av.xml"
                      />
                      <button
                        type="button"
                        className={`${s.btn} ${s.updateurl}`}
                        onClick={() => setFormData((prev) => ({ ...prev, feed_url: project?.xml_url || prev.feed_url }))}
                      >
                        Вставить от PulseADS
                      </button>
                    </div>

                    <div className={s.rowCompact}>
                      <span className={s.meta}>Email для отчетов (обязательно, можно указать любой):</span>
                    </div>
                    <div className={s.row}>
                      <input
                        className={s.input}
                        name="report_email"
                        type="email"
                        value={formData.report_email}
                        onChange={handleChange}
                        placeholder="Email для отчетов"
                        required
                      />
                    </div>

                    {!formData.report_email.trim() ? (
                      <div className={s.submitErrorBox}>
                        Для внесения изменений укажите email. Можно указать любой.
                      </div>
                    ) : null}

                    <button
                      type="button"
                      className={s.sectionBtn}
                      onClick={() => setShowScheduleEditor((prev) => !prev)}
                    >
                      ⚙ Настроить расписание и лимиты (дни, часы, объявления)
                    </button>

                    {showScheduleEditor ? (
                      <div className={s.rangeList}>
                        <div className={s.blockHead}>
                          <span className={s.meta}>Настройки расписания</span>
                        </div>

                        {formData.schedule.map((item, index) => (
                          <div className={s.rangeCard} key={`${project.id}_${index}`}>
                            <label className={s.field}>
                              <span className={s.meta}>Лимит обработки объявлений</span>
                              <input
                                className={s.input}
                                type="number"
                                value={item.rate}
                                onChange={(event) => handleRateChange(index, event.target.value)}
                              />
                            </label>

                            <div className={s.rowCompact}>
                              <span className={s.meta}>Когда авито будет обновлять данные:</span>
                            </div>

                            <div className={s.selectorBlock}>
                              <div className={s.selectorHead}>
                                <span className={s.meta}>Дни недели</span>
                                <div className={s.inlineActions}>
                                  <button
                                    type="button"
                                    className={s.textButton}
                                    onClick={() => handleScheduleBulk(index, 'weekdays', WEEKDAYS.map((day) => day.value))}
                                  >
                                    Все
                                  </button>
                                  <button
                                    type="button"
                                    className={s.textButton}
                                    onClick={() => handleScheduleBulk(index, 'weekdays', [])}
                                  >
                                    Очистить
                                  </button>
                                </div>
                              </div>

                              <div className={s.chipsGrid}>
                                {WEEKDAYS.map((day) => {
                                  const checked = item.weekdays.includes(day.value);
                                  return (
                                    <label key={day.value} className={`${s.chip} ${checked ? s.chipActive : ''}`}>
                                      <input type="checkbox" checked={checked} onChange={() => handleToggleWeekday(index, day.value)} />
                                      <span>{day.label}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>

                            <div className={s.selectorBlock}>
                              <div className={s.selectorHead}>
                                <span className={s.meta}>Часы запуска</span>
                                <div className={s.inlineActions}>
                                  <button
                                    type="button"
                                    className={s.textButton}
                                    onClick={() => handleScheduleBulk(index, 'time_slots', [...ALL_HOURS])}
                                  >
                                    Все
                                  </button>
                                  <button
                                    type="button"
                                    className={s.textButton}
                                    onClick={() => handleScheduleBulk(index, 'time_slots', [])}
                                  >
                                    Очистить
                                  </button>
                                </div>
                              </div>

                              <div className={s.hoursGrid}>
                                {ALL_HOURS.map((hour) => {
                                  const checked = item.time_slots.includes(hour);
                                  return (
                                    <label key={hour} className={`${s.chip} ${checked ? s.chipActive : ''}`}>
                                      <input type="checkbox" checked={checked} onChange={() => handleToggleHour(index, hour)} />
                                      <span>{String(hour).padStart(2, '0')}:00</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </>
                )}
              </section>
            </>
          )}
        </div>

        <div className={s.footer}>
          {submitError ? <div className={s.submitErrorBox}>{submitError}</div> : null}
          <button type="button" className={`${s.btn} ${s.secondary}`} onClick={onClose} disabled={isSaving}>
            Отмена
          </button>
          <button
            type="button"
            className={`${s.btn} ${s.primary}`}
            onClick={handleSubmit}
            disabled={isSaving || !profileLoadedOk}
          >
            {isSaving ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AutoloadSettingsModal;
